import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthShell from '../../components/auth/AuthShell';
import { Loader2, AlertCircle, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (pw !== pw2) { setError('Passwords do not match'); return; }
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav('/login', { replace: true }), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not reset password.');
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <AuthShell title="Invalid reset link" testId="reset-password-page-invalid"
        footer={<Link to="/forgot-password" className="text-[#00D4FF] hover:underline">Request a new link</Link>}>
        <p className="text-sm text-gray-400">This reset link is missing a token. Please request a new one.</p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Choose a new password" testId="reset-password-page"
      footer={<Link to="/login" className="text-[#00D4FF] hover:underline">Back to sign in</Link>}>
      {done ? (
        <div className="text-center py-6" data-testid="reset-done">
          <div className="w-14 h-14 rounded-full bg-[#00D4FF]/15 flex items-center justify-center mx-auto mb-4">
            <Check className="h-6 w-6 text-[#00D4FF]" />
          </div>
          <p className="text-sm font-semibold">Password updated</p>
          <p className="text-[11px] text-gray-500 mt-1">Redirecting you to sign in…</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">New password</label>
            <input type="password" required value={pw} onChange={e => setPw(e.target.value)}
                   data-testid="reset-pw-input"
                   className="w-full mt-1.5 px-4 py-3 rounded-lg bg-[#0A0A0A] border border-[#1f1f1f] focus:border-[#00D4FF] text-sm outline-none" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Confirm password</label>
            <input type="password" required value={pw2} onChange={e => setPw2(e.target.value)}
                   data-testid="reset-pw2-input"
                   className="w-full mt-1.5 px-4 py-3 rounded-lg bg-[#0A0A0A] border border-[#1f1f1f] focus:border-[#00D4FF] text-sm outline-none" />
          </div>
          {error && (
            <div className="flex items-start gap-2 text-[11px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="reset-error">
              <AlertCircle className="h-3.5 w-3.5 mt-px" /> <span>{error}</span>
            </div>
          )}
          <button type="submit" disabled={loading} data-testid="reset-submit-btn"
                  className="w-full py-3.5 rounded-lg bg-[#FF4D94] text-white font-bold text-sm disabled:opacity-60 flex items-center justify-center gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reset password'}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
